import { FC, useContext, useEffect, useRef, useState } from 'react';
import useRecaptcha from 'hooks/useRecaptcha';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/ui/Button/Button';
import ExpiredToken from 'components/shared/ExpiredToken';
import * as idb from 'utils/shared/idbUrl';
import usePageInMemory from 'hooks/usePageInMemory';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useGetPage } from 'hooks/useGetPage';
import { getFlights, getHotels, search } from 'services/dynamic';
import { DynamicEventResponse, GrossAndTax } from 'ts/dynamic/interfaces/DynamicResponse';
import { DynamicContext } from 'contexts/DynamicContext';
import DynamicDetails from './DynamicDetails';
import { SearchDynamicModel } from 'ts/dynamic/classes/SearchDynamicModel';
import DetailsDynamicSkeleton from './DetailsDynamicSkeleton';
import { getSortedHotels, initSingleHotel } from 'utils/dynamic/dynamic-htl';
import { getSortedFlights } from 'utils/dynamic/dynamic';
import useDynamicDataLayer from 'hooks/useDynamicDataLayer';
import { MainContext } from 'contexts/MainContext';
import { b2b } from 'constants/generic';

const DetailsDynamicFHPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleRecapcha, isRecaptchaReady } = useRecaptcha();
  const [urlParams, setUrlParams] = useSearchParams();
  const [data, setData] = useState<DynamicEventResponse>();
  const [numberOfPax, setNumberOfPax] = useState(0);
  const [updatePage, setUpdatePage] = useState('');
  const [currency, setCurrency] = useState<keyof GrossAndTax>('usd');
  const abortController = useRef(new AbortController());
  const timeoutFlightRef = useRef<any>();
  const timeoutHotelRef = useRef<any>();
  const { user } = useContext(MainContext);
  const { pathname } = useLocation();
  const location = useLocation();
  const idbKey = location.pathname + location.search;
  const { pushDynamicProductDetails, pushImpression } = useDynamicDataLayer();
  useGetPage('Dynamic/Details');
  const stateRef = useRef<any>();
  stateRef.current = {
    data,
    currency,
    numberOfPax,
  };

  const { onClickSearch } = usePageInMemory(idbKey, () => saveState());
  const matrixConfig = urlParams.get('matrixConfig')?.toLowerCase() || '';

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      abortController.current.abort();
      clearTimeout(timeoutFlightRef.current);
      clearTimeout(timeoutHotelRef.current);
    }; // eslint-disable-line
  }, []);

  useEffect(() => {
    (async () => {
      if (b2b && pathname !== '/dynamic-fh/details') {
        return;
      }
      const pageState = await idb.getPageState(idbKey);

      if (pageState && pageState.data) {
        setData(pageState.data);
        setCurrency(pageState.currency);
        setNumberOfPax(pageState.numberOfPax);
        idb.deletePageState(idbKey);
        return;
      }

      if (!isRecaptchaReady) return;
      const token = await handleRecapcha();
      if (!token) return;
      const gaStr = sessionStorage.getItem('dynamicFHSearchGA');
      const gaObj = gaStr ? JSON.parse(gaStr) : undefined;
      const searchTypeId = user?.config?.hotel?.searchTypeId || '138';
      const model = new SearchDynamicModel(urlParams, gaObj, +searchTypeId);
      const response = await search({ ...model, recaptchaToken: token }, abortController.current);

      if (response) {
        const oldParams = new URLSearchParams(urlParams);
        if (response.sessionExpired) {
          oldParams.delete('searchToken');
        } else {
          oldParams.set('searchToken', response.searchToken);
        }

        setUrlParams(oldParams.toString());
        pushDynamicProductDetails(response);
        pushImpression(response);
        if (response.dynamicHotel?.selectedHotel) {
          const initedHotel = await initSingleHotel(response.dynamicHotel.selectedHotel, urlParams);
          setData({
            ...response,
            dynamicHotel: {
              ...response.dynamicHotel,
              selectedHotel: initedHotel,
            },
          });
        } else {
          setData(response);
        }
        if (response.dynamicFlight?.selectedFlight) {
          const numberPax = response.dynamicFlight.selectedFlight.flightPricesByPDT.reduce(
            (acc, curr) => curr.quantity + acc,
            0
          );
          setNumberOfPax(response.paxCount || numberPax);
        }

        if (response.packageInfo?.currencyCode) {
          setCurrency(response.packageInfo.currencyCode.toLowerCase() as keyof GrossAndTax);
        }
        await initHotels(response.dynamicHotel, response.sysToken);
        await initFlights(response.dynamicFlight);
      }
    })();
  }, [isRecaptchaReady, updatePage]); // eslint-disable-line

  const initHotels = async (dynHotel: DynamicEventResponse['dynamicHotel'], sysToken: string) => {
    if (dynHotel?.searchToken) {
      const hotelResponse = await getHotels(
        dynHotel.searchToken,
        sysToken,
        abortController.current
      );
      if (hotelResponse.hotels.length) {
        setData((prev) => {
          const defaultSort = 'priceIncrease';
          const sort = sessionStorage.getItem('sorterHotels') || defaultSort;
          if (prev && prev.dynamicHotel) {
            const newHotels = getSortedHotels(
              [...(prev.dynamicHotel.allHotels || []), ...hotelResponse.hotels],
              sort
            );
            const newResult = {
              ...prev,
              dynamicHotel: {
                ...prev.dynamicHotel,
                allHotels: newHotels,
              },
            };

            if (!hotelResponse.poolingFinished) {
              timeoutHotelRef.current = setTimeout(() => initHotels(newResult.dynamicHotel, sysToken), 4000);
            }

            return newResult;
          } else {
            return prev;
          }
        });
      }
    }
  };

  const initFlights = async (dynFlight: DynamicEventResponse['dynamicFlight']) => {
    if (dynFlight?.searchToken) {
      const flightsResponse = await getFlights(
        dynFlight.searchToken,
        dynFlight.allFlights.length,
        abortController.current
      );

      if (flightsResponse) {
        const sort = 'priceIncrease';
        setData((prev) => {
          if (prev && prev.dynamicFlight) {
            const newFlights = getSortedFlights(
              [...(prev.dynamicFlight.allFlights || []), ...flightsResponse.flights],
              sort
            );
            const newResult = {
              ...prev,
              dynamicFlight: {
                ...prev.dynamicFlight,
                allFlights: newFlights,
              },
            };

            if (!flightsResponse.poolingFinished) {
              timeoutFlightRef.current = setTimeout(
                () => initFlights(newResult.dynamicFlight),
                4000
              );
            }

            return newResult;
          } else {
            return prev;
          }
        });
      }
    }
  };

  const saveState = () => {
    idb.savePageState(idbKey, stateRef.current);
  };

  return (
    <DynamicContext.Provider value={{ currency, setCurrency, numberOfPax, setNumberOfPax }}>
      {!!data &&
        !data.hasErrors &&
        !data.sessionExpired &&
        !data.attractionNotFound &&
        !data.hotelNotFound &&
        !data.flightNotFound && (
          <DynamicDetails
            data={data}
            onChangeData={setData}
            onUpdate={(str) => setUpdatePage(new Date().getTime().toString() + (str ? str : ''))}
            onSuccessBook={saveState}
            onClickBack={() => navigate('/dynamic?MatrixConfig=' + matrixConfig)}
            onClickSearch={() =>
              onClickSearch(matrixConfig, '/dynamic?MatrixConfig=' + matrixConfig)
            }
          />
        )}
      {!data && <DetailsDynamicSkeleton />}
      {data && data.sessionExpired && (
        <ExpiredToken storePath={'/dynamic?MatrixConfig=' + matrixConfig} />
      )}

      {!!data && (data.hotelNotFound || data.flightNotFound || data.hasErrors) && (
        <div className="expired">
          <div className="title mb-10">{t('dyn.no_avail')}</div>
          {data.hotelNotFound && <div className="mb-10">{t('dyn.hotel_not_found')}</div>}
          {data.flightNotFound && <div className="mb-10">{t('dyn.flight_not_found')}</div>}
          {data.hasErrors && <div className="mb-10">{t('dyn.error')}</div>}
          <Button
            title={t('dyn.search_again')}
            onClick={() => navigate('/dynamic?MatrixConfig=Dynamic_FMHM')}
            icon={faMagnifyingGlass}
            iconclassname="mr-5"
          />
        </div>
      )}
    </DynamicContext.Provider>
  );
};

export default withErrorBoundary(DetailsDynamicFHPage, 'DetailsDynamicFHPage');
